// ===================================
// TRENCH QUEST - Situation Panel (Adventure Game Style)
// Police Quest / King's Quest style free-form input
// ===================================

import { FC, useState, useEffect, useRef } from 'react'
import { RetroButton, PixelText } from '@/components/retro'
import { LoadingProgress } from './LoadingProgress'
import type { GeneratedSituation, GeneratedOutcome } from '@/types'

// Microphone icon for voice input
const MicIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
)

interface Props {
  situation: GeneratedSituation | null
  outcome: GeneratedOutcome | null
  isLoading: boolean
  onCustomAction: (action: string) => void
  onJumpToPanel?: () => void
  // Voice input props
  transcript?: string
  isListening?: boolean
  isSTTSupported?: boolean
  onStartListening?: () => void
  onStopListening?: () => void
}

export const SituationPanel: FC<Props> = ({
  situation,
  outcome,
  isLoading,
  onCustomAction,
  onJumpToPanel,
  transcript = '',
  isListening = false,
  isSTTSupported = false,
  onStartListening,
  onStopListening,
}) => {
  const [displayedText, setDisplayedText] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [showInput, setShowInput] = useState(false)
  const [actionText, setActionText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Update action text when voice transcript changes
  useEffect(() => {
    if (transcript) {
      setActionText(transcript)
    }
  }, [transcript])

  // Handle mic button click
  const handleMicClick = () => {
    if (isListening && onStopListening) {
      onStopListening()
    } else if (onStartListening) {
      setActionText('') // Clear text when starting voice
      onStartListening()
    }
  }

  // Typewriter effect for narrative text
  useEffect(() => {
    if (!situation && !outcome) {
      setDisplayedText('')
      setShowInput(false)
      return
    }

    const text = outcome ? outcome.narrative : situation?.description || ''
    if (!text) return

    setIsTyping(true)
    setShowInput(false)
    setActionText('')
    setDisplayedText('')

    let index = 0
    const speed = 12 // ms per character

    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1))
        index++
      } else {
        clearInterval(timer)
        setIsTyping(false)
        setTimeout(() => setShowInput(true), 200)
      }
    }, speed)

    return () => clearInterval(timer)
  }, [situation?.id, outcome?.narrative])

  // Focus input when shown
  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus()
    }
  }, [showInput])

  // Skip typewriter on click
  const handleSkipTypewriter = () => {
    if (isTyping) {
      const text = outcome ? outcome.narrative : situation?.description || ''
      setDisplayedText(text)
      setIsTyping(false)
      setTimeout(() => setShowInput(true), 100)
    }
  }

  const handleActionSubmit = () => {
    if (actionText.trim()) {
      // Just submit the action - GameProvider will handle clearing state
      onCustomAction(actionText.trim())
      setActionText('')
      setShowInput(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && actionText.trim()) {
      handleActionSubmit()
    }
  }

  // Loading state
  if (isLoading) {
    // Determine loading type based on current state
    const loadingType = outcome ? 'outcome' : 'situation'

    return (
      <div className="border-2 border-game-border bg-game-panel">
        <div className="px-3 py-1.5 bg-game-bg border-b border-game-border">
          <PixelText size="sm" color="accent">
            {loadingType === 'outcome' ? 'PROCESSING...' : 'GENERATING...'}
          </PixelText>
        </div>
        <LoadingProgress isLoading={true} type={loadingType} />
      </div>
    )
  }

  // No situation - waiting for action
  if (!situation && !outcome) {
    return (
      <div className="border-2 border-game-border bg-game-panel">
        <div className="px-3 py-1.5 bg-game-bg border-b border-game-border flex items-center justify-between">
          <PixelText size="sm" color="accent">&gt; WHAT DO YOU DO?</PixelText>
          {onJumpToPanel && (
            <button
              onClick={onJumpToPanel}
              className="lg:hidden px-2 py-0.5 font-pixel text-xs bg-game-accent text-game-bg border border-game-accent hover:bg-game-accent/80 transition-colors"
            >
              MENU
            </button>
          )}
        </div>
        <div className="px-2 py-2">
          <div className="flex items-center gap-1">
            <span className="font-mono text-game-accent">&gt;</span>
            <input
              ref={inputRef}
              type="text"
              value={actionText}
              onChange={(e) => setActionText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isListening ? "Listening..." : "look around, talk to someone, go somewhere..."}
              className={`flex-1 px-2 py-1.5 bg-game-bg border font-retro text-base text-game-text placeholder:text-game-muted/60 focus:border-game-accent focus:outline-none ${
                isListening ? 'border-red-500 animate-pulse' : 'border-game-border'
              }`}
              autoFocus
            />
            {isSTTSupported && (
              <button
                onClick={handleMicClick}
                className={`p-1.5 border transition-colors ${
                  isListening
                    ? 'bg-red-500/20 border-red-500 text-red-400'
                    : 'bg-game-panel border-game-border text-game-muted hover:text-game-accent hover:border-game-accent'
                }`}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                <MicIcon className={`w-5 h-5 ${isListening ? 'animate-pulse' : ''}`} />
              </button>
            )}
            <RetroButton
              variant="primary"
              size="sm"
              onClick={handleActionSubmit}
              disabled={!actionText.trim()}
            >
              GO
            </RetroButton>
          </div>
        </div>
      </div>
    )
  }

  // Show outcome with input
  if (outcome) {
    return (
      <div className="min-h-[120px] max-h-[50vh] border-2 border-game-border bg-game-panel flex flex-col">
        {/* Header with inline effects */}
        <div className="flex-shrink-0 px-3 py-1.5 bg-game-bg border-b border-game-border flex items-center gap-2 flex-wrap">
          <PixelText size="sm" color="accent">&gt; RESULT</PixelText>
          {outcome.effects && <EffectsSummary effects={outcome.effects} />}
        </div>

        {/* Outcome narrative - scrollable, pb-20 on mobile for fixed input bar */}
        <div
          className="flex-1 min-h-0 overflow-y-auto px-3 py-2 pb-20 lg:pb-2 cursor-pointer"
          onClick={handleSkipTypewriter}
        >
          <pre className="font-retro text-base text-game-text whitespace-pre-wrap leading-relaxed">
            {displayedText}
            {isTyping && <span className="animate-pulse">|</span>}
          </pre>
        </div>

        {/* Action input - DESKTOP ONLY (mobile uses fixed bar) */}
        {showInput && (
          <div className="hidden lg:block flex-shrink-0 px-2 py-2 border-t border-game-border bg-game-panel">
            <div className="flex items-center gap-1">
              <span className="font-mono text-game-accent">&gt;</span>
              <input
                ref={inputRef}
                type="text"
                value={actionText}
                onChange={(e) => setActionText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isListening ? "Listening..." : "What next?"}
                className={`flex-1 px-2 py-1.5 bg-game-bg border font-retro text-base text-game-text placeholder:text-game-muted/60 focus:border-game-accent focus:outline-none ${
                  isListening ? 'border-red-500 animate-pulse' : 'border-game-border'
                }`}
              />
              {isSTTSupported && (
                <button
                  onClick={handleMicClick}
                  className={`p-1.5 border transition-colors ${
                    isListening
                      ? 'bg-red-500/20 border-red-500 text-red-400'
                      : 'bg-game-panel border-game-border text-game-muted hover:text-game-accent hover:border-game-accent'
                  }`}
                  title={isListening ? 'Stop listening' : 'Voice input'}
                >
                  <MicIcon className={`w-5 h-5 ${isListening ? 'animate-pulse' : ''}`} />
                </button>
              )}
              <RetroButton
                variant="primary"
                size="sm"
                onClick={handleActionSubmit}
                disabled={!actionText.trim()}
              >
                GO
              </RetroButton>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Show situation with free-form input (adventure game style)
  return (
    <div className="min-h-[80px] lg:min-h-[120px] max-h-[40vh] lg:max-h-[50vh] border-2 border-game-border bg-game-panel flex flex-col">
      {/* Situation header */}
      <div className="flex-shrink-0 px-3 py-1.5 bg-game-bg border-b border-game-border">
        <div className="flex items-center justify-between">
          <PixelText size="sm" color="accent">&gt; {situation!.title.toUpperCase()}</PixelText>
          {onJumpToPanel && (
            <button
              onClick={onJumpToPanel}
              className="lg:hidden flex-shrink-0 px-2 py-0.5 font-pixel text-xs bg-game-accent text-game-bg border border-game-accent hover:bg-game-accent/80 transition-colors"
            >
              MENU
            </button>
          )}
        </div>
      </div>

      {/* Narrative text - scrollable, pb-20 on mobile for fixed input bar */}
      <div
        className="flex-1 min-h-0 overflow-y-auto px-3 py-2 pb-20 lg:pb-2 cursor-pointer"
        onClick={handleSkipTypewriter}
      >
        <pre className="font-retro text-base text-game-text whitespace-pre-wrap leading-relaxed">
          {displayedText}
          {isTyping && <span className="animate-pulse">|</span>}
        </pre>
      </div>

      {/* Free-form action input - DESKTOP ONLY (mobile uses fixed bar) */}
      {showInput && (
        <div className="hidden lg:block flex-shrink-0 px-2 py-2 border-t border-game-border bg-game-panel">
          <div className="flex items-center gap-1">
            <span className="font-mono text-game-accent">&gt;</span>
            <input
              ref={inputRef}
              type="text"
              value={actionText}
              onChange={(e) => setActionText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isListening ? "Listening..." : "What do you do?"}
              className={`flex-1 px-2 py-1.5 bg-game-bg border font-retro text-base text-game-text placeholder:text-game-muted/60 focus:border-game-accent focus:outline-none ${
                isListening ? 'border-red-500 animate-pulse' : 'border-game-border'
              }`}
            />
            {isSTTSupported && (
              <button
                onClick={handleMicClick}
                className={`p-1.5 border transition-colors ${
                  isListening
                    ? 'bg-red-500/20 border-red-500 text-red-400'
                    : 'bg-game-panel border-game-border text-game-muted hover:text-game-accent hover:border-game-accent'
                }`}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                <MicIcon className={`w-5 h-5 ${isListening ? 'animate-pulse' : ''}`} />
              </button>
            )}
            <RetroButton
              variant="primary"
              size="sm"
              onClick={handleActionSubmit}
              disabled={!actionText.trim()}
            >
              GO
            </RetroButton>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper components

interface EffectsSummaryProps {
  effects: GeneratedOutcome['effects']
}

// Life event type labels
const LIFE_EVENT_LABELS: Record<string, string> = {
  death: 'DEATH',
  hospitalized: 'HOSPITALIZED',
  injured: 'INJURED',
  arrested: 'ARRESTED',
  imprisoned: 'IMPRISONED',
  robbed: 'ROBBED',
  scammed: 'SCAMMED',
  lawsuit: 'LAWSUIT',
  fired: 'FIRED',
  evicted: 'EVICTED',
  windfall: 'WINDFALL',
  inheritance: 'INHERITANCE',
  gift: 'GIFT',
  lottery: 'LOTTERY WIN',
  found_money: 'FOUND MONEY',
  tax_audit: 'TAX AUDIT',
  medical_bill: 'MEDICAL BILL',
  car_accident: 'CAR ACCIDENT',
  mugged: 'MUGGED',
}

const EffectsSummary: FC<EffectsSummaryProps> = ({ effects }) => {
  const items: { label: string; value: string; color: 'profit' | 'loss' | 'accent' | 'danger' }[] = []

  // Life event - show prominently if present
  if (effects.lifeEvent) {
    const event = effects.lifeEvent
    const isPositive = event.financialImpact > 0
    const isGameEnding = event.isGameEnding
    const label = LIFE_EVENT_LABELS[event.type] || event.type.toUpperCase()
    const formattedAmount = Math.abs(event.financialImpact).toLocaleString()

    items.push({
      label: isGameEnding ? `GAME OVER: ${label}` : label,
      value: event.financialImpact !== 0
        ? `${isPositive ? '+' : '-'}$${formattedAmount}`
        : '',
      color: isGameEnding ? 'danger' : (isPositive ? 'profit' : 'loss'),
    })
  }

  if (effects.reputationChange) {
    const isPositive = effects.reputationChange > 0
    items.push({
      label: 'REP',
      value: `${isPositive ? '+' : ''}${effects.reputationChange}`,
      color: isPositive ? 'profit' : 'loss',
    })
  }

  if (effects.traitChanges) {
    for (const [trait, change] of Object.entries(effects.traitChanges)) {
      if (change && change !== 0) {
        const isPositive = change > 0
        const traitName = trait.replace('Score', '').toUpperCase().slice(0, 4)
        items.push({
          label: traitName,
          value: `${isPositive ? '+' : ''}${change}`,
          color: isPositive ? 'profit' : 'loss',
        })
      }
    }
  }

  if (effects.itemsGained && effects.itemsGained.length > 0) {
    items.push({
      label: 'ITEM',
      value: `+${effects.itemsGained.length}`,
      color: 'profit',
    })
  }

  if (effects.locationsUnlocked && effects.locationsUnlocked.length > 0) {
    items.push({
      label: 'NEW AREA',
      value: `+${effects.locationsUnlocked.length}`,
      color: 'accent',
    })
  }

  if (items.length === 0) {
    return null
  }

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {items.map((item, i) => (
        <span
          key={i}
          className={`px-1.5 py-0.5 font-pixel text-[10px] border ${
            item.color === 'profit' ? 'border-game-profit text-game-profit bg-game-profit/10' :
            item.color === 'loss' ? 'border-game-loss text-game-loss bg-game-loss/10' :
            item.color === 'danger' ? 'border-red-600 text-red-500 bg-red-600/20 animate-pulse' :
            'border-game-accent text-game-accent bg-game-accent/10'
          }`}
        >
          {item.label}{item.value ? ` ${item.value}` : ''}
        </span>
      ))}
    </div>
  )
}
